import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Play, Trash2, Youtube } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { isValidYoutubeUrl, getYouTubeEmbedUrl } from '@/lib/youtube';
import { selectV2, addVideoLink, removeVideoLink } from '../v2Upload.slice';
import { CARD_SHELL } from './fileCardStyles';
import { SectionPanel } from './FileGrids';

/** YouTube's own still for a video, no API key needed. `hqdefault` exists for
 * every video, including the ones with no custom thumbnail. */
function thumbnailFor(link: string): string | null {
  const id = getYouTubeEmbedUrl(link)?.split('/').pop();
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

function VideoCard({ link, onRemove }: { link: string; onRemove: () => void }) {
  const thumbnail = thumbnailFor(link);

  return (
    <div className={CARD_SHELL}>
      <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden">
        {thumbnail && (
          <img
            src={thumbnail}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
        <span className="absolute w-9 h-9 rounded-full bg-black/55 flex items-center justify-center pointer-events-none">
          <Play className="w-4 h-4 text-white fill-white" />
        </span>
        <Button
          variant="secondary"
          size="icon"
          title="Remove video"
          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full shadow-sm"
          onClick={onRemove}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      <div className="p-2.5">
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          title={link}
          className="block text-[10px] font-tertiary text-muted-foreground truncate hover:text-foreground"
        >
          {link}
        </a>
      </div>
    </div>
  );
}

/** YouTube links for the listing: a build video, a demo. They're links, not
 * files — nothing is uploaded, the URLs ride along on the post as
 * youtubeAttachments. */
export default function VideoLinks() {
  const dispatch = useDispatch();
  const { videoLinks } = useSelector(selectV2);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const add = () => {
    const link = url.trim();
    if (!link) return;
    if (!isValidYoutubeUrl(link)) {
      setError('That doesn’t look like a YouTube link.');
      return;
    }
    if (videoLinks.includes(link)) {
      setError('That video is already on the project.');
      return;
    }
    dispatch(addVideoLink(link));
    setUrl('');
    setError('');
  };

  return (
    <SectionPanel
      icon={Youtube}
      title="Video links — optional build or demo videos"
      help="Paste a YouTube URL. A build video is not a file, so it lives here rather than in the sections above."
      count={videoLinks.length}
      clearLabel="Remove all videos"
      onClear={() => videoLinks.forEach((l) => dispatch(removeVideoLink(l)))}
    >
      <p className="text-sm text-muted-foreground mb-4 max-w-[640px]">
        <strong>Paste a YouTube link</strong> to show the project being made or
        running.
      </p>

      <div className="flex items-center gap-3">
        <Input
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Paste a YouTube link, e.g. https://youtu.be/…"
          className="h-10 text-sm"
        />
        <Button className="shrink-0" onClick={add}>
          Add video
        </Button>
      </div>
      {error && <div className="text-xs text-amber-700 mt-2">{error}</div>}

      {videoLinks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 mt-4">
          {videoLinks.map((link) => (
            <VideoCard
              key={link}
              link={link}
              onRemove={() => dispatch(removeVideoLink(link))}
            />
          ))}
        </div>
      )}
    </SectionPanel>
  );
}
